import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Icon,
  InputCheckBox,
  SearchField,
  type SortDirection,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import EmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters'
import {
  filterFields,
  getFilterValue,
} from 'modules/core/components/EmployeeMultiFilters/commonFilters'
import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import Modal from 'modules/core/components/Modal'
import { useEmployeeGroups } from 'modules/core/components/MultiFilters/hooks/useEmployeeGroups'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import createComputed from 'modules/core/hooks/createComputed'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import usePagination from 'modules/core/hooks/usePagination'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import asyncAction from 'modules/core/lib/asyncAction'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import { employeeUser } from 'modules/core/store/collections/users'

import { employeeJobTitle } from 'modules/contracts/lib/utils'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import {
  employeeJobLevel,
  jobRoleDefaultLevel,
  jobRoleHasAssignableLevels,
} from 'modules/job_catalog/store/collections/levels'
import { employeeJobRole } from 'modules/job_catalog/store/collections/roles'
import type JobLevel from 'modules/job_catalog/store/models/JobLevel'
import type JobRole from 'modules/job_catalog/store/models/JobRole'
import Loading from 'modules/layout/components/Page/Loading'

import RoleAndLevelSelectModal from './RoleAndLevelSelectModal'
import Row, { Status } from './Row'

interface JobCatalogLevelAssignment {
  employee_id: number
  job_catalog_level_id: number | undefined
}

interface Assignment {
  employee: Employee
  role: JobRole | undefined
  level: JobLevel | undefined
  status: Status
  selected: boolean
}

interface Props {
  employees: Employee[]
  filtersForm: FiltersForm
  state: 'wrong-job-data' | 'unnasigned'
  contractVersions: ContractVersions
}

const useSearchFilter = createComputed(
  (search: string, employees: Employee[], contractVersions: ContractVersions) => {
    return employees.filter((employee) => {
      return (
        isEmpty(search) ||
        matchText(search, employeeUser(employee).firstName) ||
        matchText(search, employeeUser(employee).fullName()) ||
        matchText(search, employeeJobTitle(contractVersions, employee) ?? '')
      )
    })
  }
)

const useSortedEmployees = (employees: Employee[], attribute: string, direction: SortDirection) => {
  const sort = (employee: Employee) => {
    const value =
      (employee as any)[attribute] ??
      (employee.attributes && (employee.attributes as any)[attribute])
    return value !== undefined ? String(value) : ''
  }

  return orderBy(employees, sort, [direction])
}

const BulkEditTable: React.FC<Props> = ({ employees, filtersForm, contractVersions }) => {
  const [openModal, toggle] = useToggle(false)
  const [exitModal, setExitModal] = useToggle(false)
  const [showTooltip, toggleShowTooltip] = useToggle(false)
  const [allEmployeesSelected, setAllEmployeesSelected] = React.useState<boolean>(false)
  const [sort, setSort] = React.useState<{ direction: SortDirection; attribute: string }>({
    direction: 'asc',
    attribute: 'fullName',
  })
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const filterKey = LOCAL_STORAGE_KEYS.employeesFilters

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, filterKey)

  const searchedEmployees = useSearchFilter(
    debouncedSearch,
    filteredEmployees,
    contractVersions
  ).get()
  const sortedEmployees = useSortedEmployees(searchedEmployees, sort.attribute, sort.direction)

  const {
    paginatedItems: paginatedEmployees,
    loadMore,
    hasMore,
    page,
  } = usePagination({
    batchSize: 50,
    items: sortedEmployees,
  })
  const employeesIdsToRender = paginatedEmployees.map((employee) => employee.id)

  const sortAttribute = filtersForm.get('sortAttribute').value
  const sortDirection = filtersForm.get('sortDirection').value

  const [assignments, setAssignments] = React.useState<Record<string, Assignment>>({})

  useUnsafeEffect(() => {
    const nextAssignments = { ...assignments }

    searchedEmployees.forEach((employee) => {
      const assignment = nextAssignments[employee.id.toString()]

      if (!assignment) {
        nextAssignments[employee.id.toString()] = {
          employee: employee,
          role: employeeJobRole(contractVersions, employee),
          level: employeeJobLevel(contractVersions, employee),
          status: Status.OpenToChanges,
          selected: false,
        }
      }
    })

    setAssignments(nextAssignments)
  }, [searchedEmployees])
  const { employeeGroups } = useEmployeeGroups()

  const assignmentObjects = Object.values(assignments)

  const selectedEmployees = assignmentObjects
    .filter((assignment) => assignment.selected)
    .map((assignment) => assignment.employee)
  const showEditButton = selectedEmployees.length > 0

  const onSort = (attribute: string, direction: SortDirection) => {
    setSort({ attribute, direction })
  }

  const { performAction: handleSave } = useAsyncAction(
    async () => {
      const data: JobCatalogLevelAssignment[] = []
      const changedAssignations: string[] = []
      let nextAssignments = { ...assignments }

      Object.entries(nextAssignments).forEach(([key, assignment]) => {
        if (assignment.status == Status.Changed) {
          data.push({
            employee_id: assignment.employee.id,
            job_catalog_level_id: assignment.level?.id,
          })
          nextAssignments[key] = { ...assignment, status: Status.Loading }
          changedAssignations.push(key)
        }
      })

      setAssignments(nextAssignments)

      const { success, value } = await asyncAction(
        () => contractVersions.rpc('bulk_assign_job_catalog_levels', { assignments: data }),
        {
          successFlash: false,
          errorFlash: true,
        }
      )

      nextAssignments = { ...assignments }
      changedAssignations.forEach((key) => {
        const assignment = nextAssignments[key]!
        assignment.status = success ? Status.Success : Status.ServerError
        assignment.selected = false
        nextAssignments[key] = { ...assignment }
      })

      setAssignments(nextAssignments)

      if (success) {
        const cv_ids = value.map((cv: ContractVersion) => cv.id)

        await contractVersions.fetch({
          data: {
            contract_version_ids: cv_ids,
          },
        })

        openModal && toggle(false)()
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])
  const { loading } = useAsyncData(async () => {
    const fields = await filterFields(employeeGroups)

    setFields(fields)
  }, [])

  const handleSaveModal = (role: JobRole, level: JobLevel) => {
    const nextAssignments = { ...assignments }
    Object.entries(assignments)
      .filter(([_, assignment]) => assignment.selected)
      .forEach(([key, assignment]) => {
        assignment.role = role
        assignment.level = level
        assignment.status = Status.Changed
        assignment.selected = false

        nextAssignments[key] = { ...assignment }
      })

    setAssignments(nextAssignments)
    setAllEmployeesSelected(false)
    handleSave()
  }

  const handleChange = (key: string, assignment: Assignment) => {
    const nextAssignments = { ...assignments }
    nextAssignments[key] = { ...assignment }

    setAssignments(nextAssignments)
    handleSave()
  }

  const handleRoleChange = (key: string, role: JobRole | undefined) => {
    const level = !role || jobRoleHasAssignableLevels(role) ? undefined : jobRoleDefaultLevel(role)

    const assignment = assignments[key]!
    assignment.role = role
    assignment.level = level
    assignment.status = !role || level ? Status.Changed : Status.OpenToChanges

    handleChange(key, assignment)
  }

  const handleLevelChange = (key: string, level: JobLevel | undefined) => {
    const assignment = assignments[key]!
    assignment.level = level

    handleChange(key, assignment)
  }

  const handleToggleSelection = (key: string, isSelected: boolean) => {
    const assignment = assignments[key]!
    assignment.selected = isSelected
    handleChange(key, assignment)
    if (allEmployeesSelected && !isSelected) setAllEmployeesSelected(false)
  }

  const toggleAllSelected = () => {
    setAllEmployeesSelected(!allEmployeesSelected)

    const nextAssignments = { ...assignments }
    Object.keys(nextAssignments).forEach((key) => {
      const assignment = nextAssignments[key]!
      nextAssignments[key] = { ...assignment, selected: !allEmployeesSelected }
    })

    setAssignments(nextAssignments)
  }

  const handleSelectionFinished = (key: string) => {
    const assignment = assignments[key]!
    const invalidLevel = assignment.role && !assignment.level

    assignment.role = invalidLevel
      ? employeeJobRole(contractVersions, assignment.employee)
      : assignment.role
    assignment.level = invalidLevel
      ? employeeJobLevel(contractVersions, assignment.employee)
      : assignment.level
    assignment.status = invalidLevel ? Status.InvalidLevel : Status.Changed

    handleChange(key, assignment)
  }

  const options = () => {
    if (loading) {
      return <Loading />
    }

    return (
      <Box
        justifyContent="spaceBetween"
        flexDirection="row"
        width="full"
        alignItems="center"
        gap="s8"
      >
        <Box flexDirection="row" width="full" gap="s6">
          <EmployeeMultiFilters
            label={i18n.t('filters.multi_filters.title')}
            fields={fields}
            filters={filters}
            logicOperator={logicOperator}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onApplyFilters={(defaultFilter) => onApplyFilters(employees, defaultFilter)}
            onCancel={onCancel}
            filterKey={filterKey}
            filterDependencies={[sortAttribute, sortDirection]}
            showDefaultFilters
          />
          <SearchField
            variant="big"
            onSearch={onSearch}
            search={search}
            placeholder={i18n.t('common.search')}
          />
        </Box>
        <Box alignSelf="center" paddingLeft="s8" flexNoShrink>
          <Button disabled={!showEditButton} type="primary" onClick={toggle(true)}>
            {i18n.t('job_catalog.bulk_edit.modify_selection')}
          </Button>
        </Box>
      </Box>
    )
  }

  const TableCheckBox = () => {
    return (
      <Box gap="s12" paddingRight="s8">
        <InputCheckBox checked={allEmployeesSelected} onClick={toggleAllSelected} />
      </Box>
    )
  }

  const rows = employeesIdsToRender.map((employeeId) => {
    const assignment = assignments[employeeId]
    if (!assignment) return null
    return (
      <Row
        key={employeeId}
        rowKey={employeeId}
        employee={assignment.employee}
        status={assignment.status}
        role={assignment.role}
        level={assignment.level}
        selected={assignment.selected}
        onToggleSelection={handleToggleSelection}
        onRoleChange={handleRoleChange}
        onLevelChange={handleLevelChange}
        onSelectionFinished={handleSelectionFinished}
      />
    )
  })

  return (
    <Table grid overflowY="auto" options={options} fullWidth>
      <Table.Head>
        <Table.Row>
          <Table.SortableHeadCell
            left={<TableCheckBox />}
            attribute="fullName"
            onSort={onSort}
            sortAttribute={sort.attribute}
            sortDirection={sort.direction}
            colSpan={2}
          >
            {i18n.t('common.employee')}
          </Table.SortableHeadCell>
          <Table.SortableHeadCell
            attribute="jobTitle"
            onSort={onSort}
            sortAttribute={sort.attribute}
            sortDirection={sort.direction}
            colSpan={2}
          >
            {i18n.t('common.job_title')}
          </Table.SortableHeadCell>
          <Table.HeadCell colSpan={3}>
            <Text.Normal weight="medium" size="200" color="grey800">
              {i18n.t('job_catalog.job_role_header')} &nbsp;
            </Text.Normal>
            <Tooltip.Alt
              open={showTooltip}
              toggle={toggleShowTooltip}
              attachment="top center"
              label={i18n.t('job_catalog.bulk_edit.job_header_tooltip')}
            >
              <Icon.Small icon="question" encircled="grey800" />
            </Tooltip.Alt>
          </Table.HeadCell>
        </Table.Row>
      </Table.Head>
      <Table.BodyWithInfiniteScroll page={page} loadMore={loadMore} hasMore={hasMore} colSpan={2}>
        {rows.length ? (
          rows
        ) : (
          <Table.Blank colSpan={7} text={i18n.t('employees.no_employees_found')} />
        )}
      </Table.BodyWithInfiniteScroll>
      {openModal && (
        <RoleAndLevelSelectModal
          onClose={toggle(false)}
          onSave={handleSaveModal}
          employees={Array.from(selectedEmployees)}
        />
      )}
      {exitModal && (
        <Modal
          title={i18n.t('job_catalog.bulk_edit.exit_modal_title')}
          illustration="alert"
          description={i18n.t('job_catalog.bulk_edit.exit_modal_description')}
          onSubmit={() => navigate(navigation.employees.jobCatalog.list.fullPath())}
          onClose={setExitModal(false)}
          footer={
            <>
              <Button type="secondary" onClick={setExitModal(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button submit>{i18n.t('common.exit')}</Button>
            </>
          }
        />
      )}
    </Table>
  )
}

export default observer(BulkEditTable)
